// Trade-ins — old parts CAP took in when a customer chose the exchange price.
// Each is disposed of: sold whole, dismantled (inner parts sold), or scrapped —
// logging the resale amount(s) so we see recovery vs the discount we gave.

import React, { useCallback, useEffect, useState } from 'react';
import {
  FiAlertCircle,
  FiCheckCircle,
  FiDollarSign,
  FiPackage,
  FiRefreshCw,
  FiRepeat,
  FiSearch,
  FiSettings,
  FiTrendingUp,
  FiX,
} from 'react-icons/fi';
import MainLayout from '../components/layout/MainLayout.jsx';
import { Alert, Button } from '../components/common/index.js';
import { tradeInApi } from '../api/stockApi';

const fmtMoney = (v) => {
  if (v == null || v === '') return '—';
  const n = Number(v);
  return Number.isFinite(n) ? `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}` : '—';
};

const DISPOSITIONS = [
  { value: 'pending', label: 'In stock' },
  { value: 'sold_whole', label: 'Sold whole' },
  { value: 'dismantled', label: 'Dismantled (inner parts)' },
  { value: 'scrapped', label: 'Scrapped' },
];
const DISPO_BADGE = {
  pending: 'border-amber-200 bg-amber-50 text-amber-700',
  sold_whole: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  dismantled: 'border-violet-200 bg-violet-50 text-violet-700',
  scrapped: 'border-slate-200 bg-slate-100 text-slate-600',
};
const DISPO_LABEL = Object.fromEntries(DISPOSITIONS.map((d) => [d.value, d.label]));

const TABS = [{ value: '', label: 'All' }, ...DISPOSITIONS];

const StatCard = ({ icon: Icon, label, value, tone }) => {
  const tones = {
    amber: 'bg-amber-50 text-amber-600',
    rose: 'bg-rose-50 text-rose-600',
    sky: 'bg-sky-50 text-sky-600',
    emerald: 'bg-emerald-50 text-emerald-600',
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${tones[tone]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="truncate text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</p>
          <p className="truncate text-lg font-bold text-slate-900">{value}</p>
        </div>
      </div>
    </div>
  );
};

const ManageModal = ({ row, busy, error, onClose, onUpdate, onAddSale, onRemoveSale }) => {
  const [name, setName] = useState(row.name || '');
  const [note, setNote] = useState(row.note || '');
  const [saleName, setSaleName] = useState('');
  const [saleAmount, setSaleAmount] = useState('');
  const [saleDate, setSaleDate] = useState('');
  const [localErr, setLocalErr] = useState('');

  const sales = row.sales ?? [];
  const profitNeg = Number(row.profit) < 0;

  const addSale = async () => {
    setLocalErr('');
    const amt = Number(saleAmount);
    if (saleAmount === '' || !Number.isFinite(amt) || amt < 0) { setLocalErr('Enter a valid amount'); return; }
    const body = { item_name: saleName.trim() || (row.disposition === 'scrapped' ? 'Scrap' : 'Whole'), amount: amt };
    if (saleDate) body.sold_at = saleDate;
    const ok = await onAddSale(body);
    if (ok) { setSaleName(''); setSaleAmount(''); setSaleDate(''); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" onClick={onClose}>
      <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-bold text-slate-900">{row.name}</h3>
            <p className="mt-0.5 text-sm text-slate-500">{row.car || '—'} · {row.customer_name || '—'}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
            <FiX className="h-4 w-4" />
          </button>
        </div>

        {error && <div className="mt-3"><Alert type="error" message={error} /></div>}

        {/* Money picture */}
        <div className="mt-4 grid grid-cols-3 gap-2">
          <div className="rounded-lg bg-slate-50 px-3 py-2">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Discount given</p>
            <p className="mt-0.5 text-sm font-bold text-rose-600">{fmtMoney(row.discount_given)}</p>
          </div>
          <div className="rounded-lg bg-slate-50 px-3 py-2">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Recovered</p>
            <p className="mt-0.5 text-sm font-bold text-emerald-600">{fmtMoney(row.resale_amount)}</p>
          </div>
          <div className="rounded-lg bg-slate-50 px-3 py-2">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Profit</p>
            <p className={`mt-0.5 text-sm font-bold ${profitNeg ? 'text-rose-600' : 'text-emerald-600'}`}>{fmtMoney(row.profit)}</p>
          </div>
        </div>

        {/* Disposition */}
        <p className="mt-4 mb-1.5 text-sm font-semibold text-slate-700">Outcome</p>
        <div className="grid grid-cols-2 gap-2">
          {DISPOSITIONS.map((d) => (
            <button
              key={d.value} type="button" disabled={busy}
              onClick={() => onUpdate({ disposition: d.value })}
              className={`rounded-xl border px-3 py-2 text-sm font-bold transition-all ${row.disposition === d.value ? 'border-violet-500 bg-violet-600 text-white' : 'border-slate-200 bg-white text-slate-600 hover:border-violet-300'}`}
            >
              {d.label}
            </button>
          ))}
        </div>

        {/* Collection toggle — for a cash-back trade-in, marking it collected
            opens the refund of the difference for admin to settle. */}
        <label className="mt-4 flex cursor-pointer items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-4 py-3">
          <input type="checkbox" checked={row.received} disabled={busy}
                 onChange={(e) => onUpdate({ received: e.target.checked })}
                 className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-400" />
          <span className="text-sm font-semibold text-slate-700">Old part collected (physically received)</span>
        </label>
        {row.settlement === 'cashback' && (
          <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs font-semibold text-amber-800">
            🔁 Cash-back deal — customer paid full; refund {fmtMoney(row.cashback_refund?.amount ?? row.discount_given)} of the difference.
            {row.cashback_refund
              ? row.cashback_refund.paid
                ? ' Refund settled ✓.'
                : ' Refund is open on the Refunds screen to settle.'
              : row.received
                ? ''
                : ' Marking it collected opens the refund.'}
          </div>
        )}

        {/* Resale ledger */}
        <div className="mt-4 rounded-xl border border-slate-200 p-3">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-700">Resale ledger</p>
            <p className="text-sm font-bold text-slate-900">{fmtMoney(row.resale_amount)}</p>
          </div>
          {sales.length > 0 ? (
            <ul className="mb-2 space-y-1.5">
              {sales.map((s) => (
                <li key={s.id} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm">
                  <span className="font-semibold text-slate-800">{s.item_name}</span>
                  <span className="flex-1 truncate text-right text-xs text-slate-400">{s.sold_at_display}{s.note ? ` · ${s.note}` : ''}</span>
                  <span className="font-bold text-slate-900">{fmtMoney(s.amount)}</span>
                  <button type="button" onClick={() => onRemoveSale(s.id)} disabled={busy}
                    className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-50" title="Remove">
                    <FiX className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mb-2 text-xs text-slate-400">No resale yet — add what you got below.</p>
          )}
          {localErr && <p className="mb-2 text-xs font-semibold text-rose-600">{localErr}</p>}
          <div className="flex flex-wrap items-center gap-2">
            <input type="text" value={saleName} onChange={(e) => setSaleName(e.target.value)} disabled={busy}
              placeholder={row.disposition === 'scrapped' ? 'Scrap' : row.disposition === 'dismantled' ? 'Inner part' : 'Whole'}
              className="min-w-0 flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300" />
            <div className="relative w-28">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-400">₹</span>
              <input type="number" min="0" step="any" inputMode="numeric" value={saleAmount} onChange={(e) => setSaleAmount(e.target.value)} disabled={busy}
                placeholder="Amount" className="w-full rounded-xl border border-slate-200 py-2 pl-7 pr-2 text-sm font-semibold focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300" />
            </div>
            <input type="date" value={saleDate} onChange={(e) => setSaleDate(e.target.value)} disabled={busy}
              className="rounded-xl border border-slate-200 px-2 py-2 text-sm text-slate-600 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300" />
            <Button type="button" variant="secondary" onClick={addSale} loading={busy} disabled={busy}>Add</Button>
          </div>
        </div>

        {/* Name + note */}
        <div className="mt-4 space-y-3">
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-slate-700">Part name</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} onBlur={() => name.trim() && name !== row.name && onUpdate({ name: name.trim() })} disabled={busy}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300" />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-slate-700">Note</label>
            <input type="text" value={note} onChange={(e) => setNote(e.target.value)} onBlur={() => note !== row.note && onUpdate({ note })} disabled={busy}
              placeholder="e.g. sold engine block to XYZ, kept alternator"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300" />
          </div>
        </div>

        <div className="mt-5 flex justify-end">
          <Button type="button" variant="primary" onClick={onClose}>Done</Button>
        </div>
      </div>
    </div>
  );
};

const TradeInsPage = () => {
  const [disposition, setDisposition] = useState('');
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [target, setTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const [modalError, setModalError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await tradeInApi.getList({ disposition: disposition || undefined, q: q || undefined });
      setData(res.data ?? res);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Could not load trade-ins');
    } finally {
      setLoading(false);
    }
  }, [disposition, q]);

  useEffect(() => { load(); }, [load]);

  const update = async (patch) => {
    setBusy(true); setModalError('');
    try {
      const res = await tradeInApi.update(target.id, patch);
      setTarget((res.data ?? res).trade_in);
      load();
    } catch (e) {
      setModalError(e?.response?.data?.error || e.message || 'Update failed');
    } finally { setBusy(false); }
  };
  const addSale = async (body) => {
    setBusy(true); setModalError('');
    try {
      const res = await tradeInApi.addSale(target.id, body);
      setTarget((res.data ?? res).trade_in);
      load();
      return true;
    } catch (e) {
      setModalError(e?.response?.data?.error || e.message || 'Could not add resale');
      return false;
    } finally { setBusy(false); }
  };
  const removeSale = async (saleId) => {
    setBusy(true); setModalError('');
    try {
      const res = await tradeInApi.removeSale(target.id, saleId);
      setTarget((res.data ?? res).trade_in);
      load();
    } catch (e) {
      setModalError(e?.response?.data?.error || e.message || 'Could not remove resale');
    } finally { setBusy(false); }
  };

  const rows = data?.trade_ins ?? [];
  const counts = {
    '': rows.length,
    pending: data?.pending_count ?? 0,
    sold_whole: data?.sold_whole_count ?? 0,
    dismantled: data?.dismantled_count ?? 0,
    scrapped: data?.scrapped_count ?? 0,
  };

  return (
    <MainLayout>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-black text-slate-900">Trade-ins</h1>
          <p className="mt-1 text-sm text-slate-500">Old parts taken in on exchanges — resell whole, dismantle for parts, or scrap</p>
        </div>
        <Button variant="secondary" onClick={load} className="gap-2"><FiRefreshCw className="h-4 w-4" /> Refresh</Button>
      </div>

      {error && <div className="mb-4"><Alert type="error" message={error} /></div>}

      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard icon={FiRepeat} label="In stock" value={data?.pending_count ?? '—'} tone="amber" />
        <StatCard icon={FiDollarSign} label="Discount given" value={fmtMoney(data?.total_discount_given)} tone="rose" />
        <StatCard icon={FiTrendingUp} label="Recovered" value={fmtMoney(data?.total_resale)} tone="emerald" />
        <StatCard icon={FiCheckCircle} label="Net (recovered − cost)" value={fmtMoney(data?.total_profit)} tone="sky" />
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {TABS.map((t) => {
            const active = disposition === t.value;
            return (
              <button key={t.value || 'all'} type="button" onClick={() => setDisposition(t.value)}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-bold transition-all ${active ? 'border-violet-500 bg-violet-600 text-white shadow-sm' : 'border-slate-200 bg-white text-slate-600 hover:border-violet-300'}`}>
                {t.label}
                <span className={`rounded-full px-1.5 text-xs font-bold ${active ? 'bg-white/20' : 'bg-slate-100 text-slate-500'}`}>{counts[t.value] ?? 0}</span>
              </button>
            );
          })}
        </div>
        <div className="relative">
          <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Part, car or customer…"
            className="w-64 rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300" />
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-sm text-slate-400">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-white py-14 text-center">
          <FiPackage className="mx-auto mb-2 h-8 w-8 text-slate-200" />
          <p className="text-sm text-slate-400">No trade-ins here yet — they appear when a customer takes the exchange price and the part is handed over</p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => {
            const profitNeg = Number(row.profit) < 0;
            return (
              <div key={row.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex min-w-0 items-start gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-violet-50 text-violet-600">
                      <FiRepeat className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="truncate text-base font-bold text-slate-900">{row.name}</h3>
                      <p className="mt-0.5 text-sm text-slate-500">{row.car || '—'} · {row.customer_name || '—'}</p>
                      <p className="mt-0.5 text-xs font-medium text-slate-400">
                        Inquiry #{row.inquiry_id ?? '—'} · by {row.salesperson_name || '-'}
                        {!row.received && <span className="ml-1 font-bold text-amber-600">· old part to collect</span>}
                      </p>
                      {row.settlement === 'cashback' && (
                        <p className="mt-0.5 text-xs font-bold text-amber-700">
                          🔁 Cash-back {fmtMoney(row.cashback_refund?.amount ?? row.discount_given)}
                          {row.cashback_refund
                            ? row.cashback_refund.paid ? ' · refunded ✓' : ' · refund pending (admin settles)'
                            : ' · on collection'}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-bold ${DISPO_BADGE[row.disposition]}`}>
                      {row.disposition === 'pending' && <FiAlertCircle className="h-3.5 w-3.5" />}
                      {DISPO_LABEL[row.disposition]}
                    </span>
                    {!row.received && (
                      <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-700">
                        <FiRepeat className="h-3.5 w-3.5" /> To collect
                      </span>
                    )}
                    <Button size="sm" variant="primary" onClick={() => { setTarget(row); setModalError(''); }} className="gap-1.5">
                      <FiSettings className="h-3.5 w-3.5" /> Manage
                    </Button>
                  </div>
                </div>

                <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <div className="rounded-lg bg-slate-50 px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Normal → Exchange</p>
                    <p className="mt-0.5 text-sm font-bold text-slate-800">{fmtMoney(row.normal_price)} → {fmtMoney(row.exchange_price)}</p>
                  </div>
                  <div className="rounded-lg bg-slate-50 px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Discount given</p>
                    <p className="mt-0.5 text-sm font-bold text-rose-600">{fmtMoney(row.discount_given)}</p>
                  </div>
                  <div className="rounded-lg bg-slate-50 px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Recovered</p>
                    <p className="mt-0.5 text-sm font-bold text-emerald-600">{fmtMoney(row.resale_amount)}</p>
                  </div>
                  <div className="rounded-lg bg-slate-50 px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Profit</p>
                    <p className={`mt-0.5 text-sm font-bold ${profitNeg ? 'text-rose-600' : 'text-emerald-600'}`}>{fmtMoney(row.profit)}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {target && (
        <ManageModal
          row={target} busy={busy} error={modalError}
          onClose={() => { setTarget(null); setModalError(''); }}
          onUpdate={update} onAddSale={addSale} onRemoveSale={removeSale}
        />
      )}
    </MainLayout>
  );
};

export default TradeInsPage;
